import React, { useMemo, useState } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { IoArrowBack } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

const validateEmail = (value) => {
  if (!value) {
    return "Please enter your email address.";
  }
  if (!/\S+@\S+\.\S+/.test(value)) {
    return "Please enter a valid email address.";
  }
  return "";
};

const validatePhone = (value) => {
  if (!value) {
    return "Please enter your phone number.";
  }
  if (value.length !== 11) {
    return "Phone number must be 11 digits.";
  }
  return "";
};

const validatePassword = (value) => {
  if (!value) {
    return "Please create a password.";
  }
  if (value.length < 8) {
    return "Password must be at least 8 characters.";
  }
  return "";
};

const validatePasswordMatch = (password, confirm) => {
  if (!confirm) {
    return "Please confirm your password.";
  }
  if (password !== confirm) {
    return "Passwords do not match.";
  }
  return "";
};

const StudentResellerScreen = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    basic: {
      fullName: "",
      email: "",
      phone: "",
      gender: "",
    },
    business: {
      storeName: "",
      communityGender: "",
      preferredCategory: "",
      pricingTier: "",
    },
    location: {
      state: "Delta",
      city: "Abraka",
      address: "",
    },
    password: {
      create: "",
      confirm: "",
    },
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [backendMsg, setBackendMsg] = useState("");
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleFieldChange = (section, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
    setErrors((prev) => ({ ...prev, [`${section}.${field}`]: "" }));
  };

  const handlePhoneChange = (event) => {
    const value = event.target.value.replace(/\D/g, "").slice(0, 11);
    setFormData((prev) => ({
      ...prev,
      basic: {
        ...prev.basic,
        phone: value,
      },
    }));
    setErrors((prev) => ({ ...prev, "basic.phone": "" }));
  };

  const handleBack = () => {
    navigate(-1);
  };

  const validateForm = () => {
    const nextErrors = {};

    const emailError = validateEmail(formData.basic.email);
    if (emailError) {
      nextErrors["basic.email"] = emailError;
    }

    const phoneError = validatePhone(formData.basic.phone);
    if (phoneError) {
      nextErrors["basic.phone"] = phoneError;
    }

    const passwordError = validatePassword(formData.password.create);
    if (passwordError) {
      nextErrors["password.create"] = passwordError;
    }

    const matchError = validatePasswordMatch(
      formData.password.create,
      formData.password.confirm
    );
    if (matchError) {
      nextErrors["password.confirm"] = matchError;
    }

    if (!termsAccepted) {
      nextErrors.terms = "Please accept the terms and conditions.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const isFormComplete = useMemo(() => {
    const { basic, business, location, password } = formData;
    const requiredFields = [
      basic.fullName,
      basic.email,
      basic.phone,
      basic.gender,
      business.storeName,
      business.communityGender,
      business.preferredCategory,
      business.pricingTier,
      location.address,
      password.create,
      password.confirm,
    ];

    if (requiredFields.some((value) => !value)) {
      return false;
    }

    if (validateEmail(basic.email)) {
      return false;
    }

    if (validatePhone(basic.phone)) {
      return false;
    }

    if (validatePassword(password.create)) {
      return false;
    }

    if (validatePasswordMatch(password.create, password.confirm)) {
      return false;
    }

    return termsAccepted;
  }, [formData, termsAccepted]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setBackendMsg("");
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    try {
      const payload = {
        fullName: formData.basic.fullName,
        email: formData.basic.email,
        phoneNumber: formData.basic.phone,
        gender: formData.basic.gender,
        storeName: formData.business.storeName,
        communityGender: formData.business.communityGender,
        preferredCategory: formData.business.preferredCategory,
        pricingTier: formData.business.pricingTier,
        state: formData.location.state,
        city: formData.location.city,
        address: formData.location.address,
        password: formData.password.create,
        confirmPassword: formData.password.confirm,
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/student-resellers/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (response.ok && result.success) {
        localStorage.setItem("studentResellerSignupEmail", formData.basic.email);
        navigate("/student-confirm-otp", {
          state: {
            email: formData.basic.email,
            successMessage: result.message || "Student reseller signup successful. A verification code has been sent to your email.",
          },
        });
      } else {
        const errorDetails = Array.isArray(result.errors) && result.errors.length
          ? ` ${result.errors.join(" ")}`
          : "";
        setBackendMsg(`${result.message || "Student reseller signup failed."}${errorDetails}`);
      }
    } catch (err) {
      setBackendMsg("An error occurred while creating the student reseller account.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-6 py-8" style={{ paddingLeft: "8px", paddingRight: "8px", paddingTop: "12px" }}>
      <div className="mx-auto w-full max-w-xl">
        <div className="flex items-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-700 hover:text-gray-900"
            aria-label="Go back"
          >
            <IoArrowBack className="h-5 w-5" />
          </button>
        </div>
        <header className="text-center">
          <h1 className="text-xl font-semibold text-gray-900">
            Welcome! Fill the form to create account
          </h1>
        </header>

        {backendMsg && (
          <div className="mb-4 text-center bg-yellow-100 text-yellow-800 px-4 py-2 rounded-lg font-semibold animate-fadeInOut">
            {backendMsg}
          </div>
        )}
        <form onSubmit={handleSubmit} className="mt-8 space-y-8">
          <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-700">
              Basic Account Details
            </h2>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 text-left">
                Full Name
              </label>
              <input
                type="text"
                value={formData.basic.fullName}
                onChange={handleFieldChange("basic", "fullName")}
                placeholder="Enter your full name"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 text-left">
                Email Address
              </label>
              <input
                type="email"
                value={formData.basic.email}
                onChange={handleFieldChange("basic", "email")}
                placeholder="student@example.com"
                className={`w-full rounded-lg border px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none ${
                  errors["basic.email"] ? "border-red-500" : "border-gray-200"
                }`}
              />
              {formData.basic.email && validateEmail(formData.basic.email) && (
                <p className="text-xs text-red-500">
                  {validateEmail(formData.basic.email)}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 text-left">
                Phone Number
              </label>
              <input
                type="tel"
                inputMode="numeric"
                value={formData.basic.phone}
                onChange={handlePhoneChange}
                placeholder="Enter phone number"
                className={`w-full rounded-lg border px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none ${
                  errors["basic.phone"] ? "border-red-500" : "border-gray-200"
                }`}
              />
              {formData.basic.phone && validatePhone(formData.basic.phone) && (
                <p className="text-xs text-red-500">
                  {validatePhone(formData.basic.phone)}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 text-left">
                Gender
              </label>
              <select
                value={formData.basic.gender}
                onChange={handleFieldChange("basic", "gender")}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
              >
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
          </section>

          <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-700">
              Business Details
            </h2>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 text-left">
                Business / Store Name
              </label>
              <input
                type="text"
                value={formData.business.storeName}
                onChange={handleFieldChange("business", "storeName")}
                placeholder="Enter business name"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
              />
            </div>
          </section>

          <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
            <div>
              <h2 className="text-sm font-semibold text-gray-700">
                Community
              </h2>
              <p className="mt-1 text-xs text-gray-500 text-left">
                This will help us position products that fits your sale
              </p>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 text-left">
                What community of gender are you close with
              </label>
              <select
                value={formData.business.communityGender}
                onChange={handleFieldChange("business", "communityGender")}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
              >
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 text-left">
                What do you know they like most
              </label>
              <select
                value={formData.business.preferredCategory}
                onChange={handleFieldChange("business", "preferredCategory")}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
              >
                <option value="">Select option</option>
                <option value="Fashion & Beauty">Fashion & Beauty</option>
                <option value="Personal Cares">Personal Cares</option>
                <option value="Phones & Accessories">Phones & Accessories</option>
                <option value="Electronics & Gadgets">Electronics & Gadgets</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 text-left">
                What's Their Pricing Tier
              </label>
              <select
                value={formData.business.pricingTier}
                onChange={handleFieldChange("business", "pricingTier")}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
              >
                <option value="">Select pricing tier</option>
                <option value="Normal">Normal - not too expensive</option>
                <option value="Classic">Classic - more taste</option>
                <option value="Premium">Premium - Premium taste</option>
              </select>
            </div>
          </section>

          <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-700">
              Location Details
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 text-left">State</label>
                <select
                  value={formData.location.state}
                  onChange={handleFieldChange("location", "state")}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
                >
                  <option value="Delta">Delta</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 text-left">City</label>
                <select
                  value={formData.location.city}
                  onChange={handleFieldChange("location", "city")}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
                >
                  <option value="Abraka">Abraka</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 text-left">
                Address
              </label>
              <input
                type="text"
                value={formData.location.address}
                onChange={handleFieldChange("location", "address")}
                placeholder="Enter address"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none"
              />
            </div>
          </section>

          <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-700">Password</h2>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 text-left">
                Create Password
              </label>
              <div className="relative">
                <input
                  type={showCreatePassword ? "text" : "password"}
                  value={formData.password.create}
                  onChange={handleFieldChange("password", "create")}
                  placeholder="Create a password"
                  className={`w-full rounded-lg border px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none ${
                    errors["password.create"]
                      ? "border-red-500"
                      : "border-gray-200"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowCreatePassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  aria-label={showCreatePassword ? "Hide password" : "Show password"}
                >
                  {showCreatePassword ? (
                    <FiEyeOff className="h-4 w-4" />
                  ) : (
                    <FiEye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {formData.password.create &&
                validatePassword(formData.password.create) && (
                  <p className="text-xs text-red-500">
                    {validatePassword(formData.password.create)}
                  </p>
                )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 text-left">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.password.confirm}
                  onChange={handleFieldChange("password", "confirm")}
                  placeholder="Confirm password"
                  className={`w-full rounded-lg border px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none ${
                    errors["password.confirm"]
                      ? "border-red-500"
                      : "border-gray-200"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? (
                    <FiEyeOff className="h-4 w-4" />
                  ) : (
                    <FiEye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {formData.password.confirm &&
                validatePasswordMatch(
                  formData.password.create,
                  formData.password.confirm
                ) && (
                  <p className="text-xs text-red-500">
                    {validatePasswordMatch(
                      formData.password.create,
                      formData.password.confirm
                    )}
                  </p>
                )}
            </div>
          </section>

          <section className="space-y-2 rounded-2xl bg-white p-5 shadow-sm">
            <label className="flex items-start gap-3 text-sm text-gray-700 text-left">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(event) => setTermsAccepted(event.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-[#19C463] focus:ring-[#19C463]"
              />
              <span>
                Accept <span className="text-[#19C463]">Terms and Conditions</span>
              </span>
            </label>
            {errors.terms && (
              <p className="text-xs text-red-500">{errors.terms}</p>
            )}
          </section>

          <div className="space-y-3">
            <button
              type="submit"
              disabled={!isFormComplete || loading}
              className={`w-full rounded-lg py-3 text-sm font-semibold transition ${
                !isFormComplete || loading
                  ? "bg-[#A3E7C1] text-[#F1F3F2]"
                  : "bg-[#19C463] text-white"
              }`}
            >
              {loading ? "Creating account..." : "Complete"}
            </button>
            <button
              type="button"
              onClick={handleBack}
              className="w-full rounded-lg bg-[#19C463] py-3 text-sm font-semibold text-white"
            >
              Back
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudentResellerScreen;
